import React, { useState, useEffect, useCallback } from 'react'
import { Card, Button, Alert, ListGroup, Badge, Row, Col, Container, Spinner } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'
import QRScanner from '../components/QRScanner'

function CheckinCoachQR() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [presences, setPresences] = useState([])
  const [scanResult, setScanResult] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [user, setUser] = useState(null)

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUser(user)
    }
    getUser()
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*, course_types (name, location)')
        .eq('id', sessionId)
        .single()
      if (error) throw error
      setSession(data)
    } catch (error) {
      console.error('Error fetching session:', error)
    }
  }, [sessionId])

  const fetchPresences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('presences')
        .select(`
          *,
          coaches:coach_id (first_name, last_name, sports, token)
        `)
        .eq('session_id', sessionId)
        .eq('role', 'coach')           // ✅ filtre role = 'coach'
        .order('timestamp', { ascending: false })
      if (error) throw error
      setPresences(data || [])
    } catch (error) {
      console.error('Error fetching presences:', error)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    const loadData = async () => {
      await fetchSession()
      await fetchPresences()
    }
    loadData()
  }, [fetchSession, fetchPresences])

  const handleScanSuccess = async (decodedText) => {
    if (scanning) return
    setScanning(true)
    setScanResult(decodedText)
    setError('')
    setSuccess('')

    try {
      // Chercher le coach par son token
      const { data: coach, error: coachError } = await supabase
        .from('coaches')
        .select('*')
        .eq('token', decodedText.trim())
        .single()

      if (coachError || !coach) {
        setError('QR Code invalide — Ce QR code ne correspond à aucun coach enregistré.')
        setScanning(false)
        return
      }

      // Vérifier si déjà présent
      const { data: existing } = await supabase
        .from('presences')
        .select('id')
        .eq('coach_id', coach.id)
        .eq('session_id', sessionId)
        .eq('role', 'coach')
        .single()

      if (existing) {
        setError(`Coach ${coach.first_name} ${coach.last_name} est déjà signé pour cette session.`)
        setScanning(false)
        return
      }

      // Enregistrer la présence
      const { error: insertError } = await supabase
        .from('presences')
        .insert([{
          coach_id: coach.id,          // ✅ coach_id
          participant_id: null,        // ✅ null pour les coachs
          session_id: sessionId,
          mode: 'qr',
          role: 'coach',              // ✅ role = 'coach'
          timestamp: new Date().toISOString()
        }])

      if (insertError) throw new Error(insertError.message)

      setSuccess(`✅ Coach ${coach.first_name} ${coach.last_name} signé avec succès !`)
      setTimeout(() => fetchPresences(), 500)
    } catch (err) {
      setError('Erreur lors du traitement : ' + err.message)
    } finally {
      setScanning(false)
    }
  }

  const handleScanError = (err) => { console.warn('Scan error:', err) }

  if (loading) return (
    <ProtectedRoute><Layout user={user}>
      <Container className="text-center py-5">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Chargement...</p>
      </Container>
    </Layout></ProtectedRoute>
  )

  if (!session) return (
    <ProtectedRoute><Layout user={user}>
      <Container>
        <Alert variant="danger">
          <h4>Session non trouvée</h4>
          <Button variant="primary" onClick={() => navigate('/')}>Retour au Dashboard</Button>
        </Alert>
      </Container>
    </Layout></ProtectedRoute>
  )

  return (
    <ProtectedRoute>
      <Layout user={user}>
        <Container fluid="lg" className="py-4">

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <Button variant="outline-secondary" onClick={() => navigate('/')} className="me-3" size="lg">◀ Retour</Button>
              <div>
                <h2 className="h3 mb-1 fw-bold">🏅 Signature Coach — QR Code</h2>
                <p className="text-muted mb-0">
                  <strong>{session.course_types?.name}</strong>
                  <span className="mx-2">•</span>📍 {session.course_types?.location}
                  <span className="mx-2">•</span>📅 {new Date(session.date).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            <Badge bg="warning" text="dark" className="fs-6 px-3 py-2">
              🏅 {presences.length} coach(s) signé(s)
            </Badge>
          </div>

          <Row className="g-4">
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="text-center mb-4">📱 Scanner le QR Code Coach</Card.Title>

                  {error && <Alert variant="danger" className="mb-3">⚠️ {error}</Alert>}
                  {success && <Alert variant="success" className="mb-3">{success}</Alert>}

                  <div className="flex-grow-1">
                    {scanning && (
                      <div className="text-center mb-3">
                        <Spinner animation="border" size="sm" variant="warning" className="me-2" />
                        <span>Validation en cours...</span>
                      </div>
                    )}
                    <QRScanner onScanSuccess={handleScanSuccess} onScanError={handleScanError} />
                  </div>

                  {scanResult && (
                    <Alert variant="info" className="mt-3 mb-0">
                      <small><strong>Dernier scan :</strong> {scanResult}</small>
                    </Alert>
                  )}
                  <div className="mt-3 text-center">
                    <small className="text-muted">Positionnez le QR code du coach dans le cadre</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="text-center mb-3">
                    🏅 Coachs Signés
                    <Badge bg="warning" text="dark" className="ms-2 fs-6">{presences.length}</Badge>
                  </Card.Title>

                  {presences.length === 0 ? (
                    <div className="text-center text-muted py-4 flex-grow-1 d-flex align-items-center justify-content-center">
                      <div>
                        <div style={{ fontSize: '3rem' }}>🏅</div>
                        <p className="mt-2 mb-0">Aucun coach encore signé</p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="flex-grow-1">
                      <ListGroup variant="flush">
                        {presences.map((presence) => (
                          <ListGroup.Item key={presence.id} className="px-0 py-2 border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <strong className="d-block">
                                  🏅 {presence.coaches?.first_name} {presence.coaches?.last_name}
                                </strong>
                                <div className="d-flex flex-wrap gap-1 mb-1">
                                  {(presence.coaches?.sports || []).map(s => (
                                    <Badge key={s} bg="primary" className="px-1" style={{ fontSize: '0.7rem' }}>{s}</Badge>
                                  ))}
                                </div>
                                <small className="text-muted">
                                  🕒 {new Date(presence.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  <span className="mx-1">•</span>
                                  {presence.mode === 'qr' ? '📱 QR Code' : '👤 Manuel'}
                                </small>
                              </div>
                              <Badge bg="success">Signé</Badge>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-top">
                    <Button variant="outline-warning" className="w-100" onClick={() => navigate(`/checkin/coach-name/${sessionId}`)} size="lg">
                      👤 Passer à la signature par nom
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Layout>
    </ProtectedRoute>
  )
}

export default CheckinCoachQR